/**
 * Panel state, data fetching, save/format/diff logic for MetadataEditorPanel.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type * as monaco from "monaco-editor";
import { getFormatApiLanguage } from "@ncoder/ui";
import type { EditorTab, FormatApi, MonacoEditorTheme } from "@ncoder/code-editor";
import {
  useMetadataItem,
  useActiveBranch,
  useSaveBranchFile,
  useAbandonBranch,
} from "@/hooks/metadata_v1";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import { useUpdateMetadataItem } from "../useUpdateMetadataItem";
import { useFormatCode } from "@/hooks/metadata_v1/useFormatCode";
import { useApexLint, useAutoFixLoop, useApexAnalysis } from "@/hooks/chat";
import { useCreateMetadataItem } from "@/hooks/chat/useCreateMetadataItem";
import { useMetadataProgressStore } from "@/stores/metadataProgressStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { graphqlRequest } from "@/services/deployment/client";
import { SEARCH_METADATA_ITEMS_QUERY } from "@/services/metadata_v1/queries";
import toast from "react-hot-toast";
import { useTheme } from "next-themes";
import { runSessionSaveWithValidation } from "./sessionSaveWithValidation";
import type { MetadataEditorPanelProps } from "./types";
import { disposeStandaloneDiffEditor } from "./disposeStandaloneDiffEditor";
import { buildCoverageMarkers } from "./buildCoverageMarkers";
import { buildPackageTabs } from "./buildPackageTabs";
import { METADATA_TABBED_EDITOR_OPTIONS } from "./metadataTabbedEditorOptions";
import { SF_EXTENSION_REGEX } from "@/utils/chat/metadataDetection";

/** Backend metadata_item ids are UUIDs; chat-seeded tabs still use this shape. */
const CANONICAL_METADATA_ITEM_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const METADATA_TYPE_EXTENSIONS: Record<string, string> = {
  ApexClass: ".cls",
  ApexTrigger: ".trigger",
  ApexPage: ".page",
  AuraDefinitionBundle: ".cmp",
  LightningComponentBundle: ".js",
  Flow: ".flow",
  CustomObject: ".object",
  PermissionSet: ".permissionset",
};

function _stripKnownMetadataExtension(fileName: string): string {
  return fileName.replace(/^.*\//, "").replace(SF_EXTENSION_REGEX, "");
}

function _fileNameForNewMetadata(fileStem: string, metadataType: string): string {
  const ext = METADATA_TYPE_EXTENSIONS[metadataType] || "";
  return ext && !fileStem.endsWith(ext) ? `${fileStem}${ext}` : fileStem;
}

export function useMetadataEditorPanel({
  openFiles,
  activeFileId,
  onActiveFileChange,
  onCloseTab,
  onClose,
  acceptedCode,
  onAcceptedCodeChange,
  onPendingFileChange: _onPendingFileChange,
  initialContent,
  onFileSaved,
  orgId,
  onLoadIntoChat,
  extraActions,
  isAnalyzing,
  onRunTest,
  isBusy,
  onCanonicalFileId,
}: MetadataEditorPanelProps) {
  const { resolvedTheme } = useTheme();
  const editorTheme: MonacoEditorTheme =
    resolvedTheme === "dark" ? "apex-dark" : "apex-light";

  const diffEditorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);

  const contentByFileIdRef = useRef<Record<string, string>>({});
  const activeFileIdRef = useRef<string | null>(null);
  const autoFixRef = useRef<{
    startAutoFix: (code: string, errorMessage: string) => void;
  } | null>(null);

  const [saveErrors, setSaveErrors] = useState<{
    message: string;
    code: string;
  } | null>(null);
  const [contentByFileId, setContentByFileId] = useState<Record<string, string>>(
    {},
  );
  const [dirtyByFileId, setDirtyByFileId] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errorExpanded, setErrorExpanded] = useState(false);
  const [diffOverlayOpen, setDiffOverlayOpen] = useState(false);
  const [diffHunkTotal, setDiffHunkTotal] = useState(0);
  const [diffHunkResolved, setDiffHunkResolved] = useState(0);
  const [originalByFileId, setOriginalByFileId] = useState<
    Record<string, string>
  >({});
  const [viewingMain, setViewingMain] = useState(false);

  const activeFile = useMemo(
    () => openFiles.find((f) => f.id === activeFileId) ?? null,
    [openFiles, activeFileId],
  );
  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);
  const selectedRetrievalId = useMetadataProgressStore((s) => s.selectedRetrievalId);

  const { artifactBadges, acceptedCodeTarget, setArtifactBadge, sessionUuid, virtualFiles, covData } = useDevAgentStore(
    useShallow((s) => ({
      artifactBadges: s.artifactBadges,
      acceptedCodeTarget: s.acceptedCodeTarget,
      setArtifactBadge: s.setArtifactBadge,
      sessionUuid: s.sessionUuid,
      virtualFiles: s.virtualFiles,
      covData: s.coverageLines,
    }))
  );
  const activeBadge = activeFileId ? artifactBadges[activeFileId] : undefined;
  const hasSeededChatContent = !!(
    activeFileId &&
    (initialContent?.[activeFileId] || virtualFiles[activeFileId])
  );
  // Session artifact ids (`art_…`) are never GraphQL metadata_item UUIDs. We must
  // treat them as virtual even before `initialContent` is seeded (e.g. first paint
  // after reopening from Workspace) — otherwise `useActiveBranch` fires with
  // `metadataItemId: "art_…"` and the backend rejects the query.
  const isArtifactSessionId = !!(
    activeFileId && activeFileId.startsWith("art_")
  );
  // Only treat as "virtual" when the tab id is not a real metadata UUID. Seeded
  // content for UUID tabs (pending file / chat) must still load main + active
  // branch from the API — otherwise the Local/Salesforce toolbar never mounts.
  const isVirtualFile = !!(
    isArtifactSessionId ||
    (activeFileId &&
      hasSeededChatContent &&
      !CANONICAL_METADATA_ITEM_ID_RE.test(activeFileId))
  );
  const {
    data: metadataFileData,
    isLoading: isLoadingMetadataFile,
    error: metadataFileError,
    refetch: refetchMetadataFile,
  } = useMetadataItem(activeFileId, {
    enabled: !!activeFileId && !isVirtualFile,
  });

  const { data: activeBranch } = useActiveBranch(activeFileId, {
    enabled: !!activeFileId && !isVirtualFile,
  });

  const { mutateAsync: createMetadataItemAsync } = useCreateMetadataItem();

  const { mutateAsync: saveBranchAsync, isPending: isBranchSaving } = useSaveBranchFile({
    onSuccess: (branch) => {
      toast.success("Saved");
      if (activeFileId) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setOriginalByFileId((prev) => ({
          ...prev,
          [activeFileId]:
            branch.content ?? contentByFileIdRef.current[activeFileId] ?? "",
        }));
      }
      if (branch.metadataItem?.fileName && branch.metadataItem?.metadataType) {
        onFileSaved?.({
          id: branch.metadataItem.id,
          fileName: branch.metadataItem.fileName,
          metadataType: branch.metadataItem.metadataType,
        });
      }
    },
    onError: (error) => toast.error(error.message || "Failed to save"),
  });

  const originalByFileIdRef = useRef<Record<string, string>>({});
  originalByFileIdRef.current = originalByFileId;

  const { mutate: abandonBranch, isPending: isAbandoning } = useAbandonBranch(
    activeFileId,
    {
      onSuccess: () => {
        const id = activeFileIdRef.current;
        if (id) {
          const main = originalByFileIdRef.current[id] ?? "";
          setContentByFileId((prev) => ({ ...prev, [id]: main }));
          setDirtyByFileId((prev) => ({ ...prev, [id]: false }));
          setViewingMain(false);
        }
        toast.success("Branch discarded");
      },
      onError: (error) => toast.error(error.message || "Failed to discard"),
    },
  );

  const saveResolveRef = useRef<(() => void) | null>(null);
  const saveRejectRef = useRef<((err: Error) => void) | null>(null);
  /** Blocks concurrent saves before React re-renders `isSaving` (double Save / Cmd+S). */
  const saveInFlightRef = useRef(false);

  contentByFileIdRef.current = contentByFileId;
  activeFileIdRef.current = activeFileId;

  const updateMetadataMutation = useUpdateMetadataItem({
    onSuccess: (data) => {
      if (saveResolveRef.current) {
        saveResolveRef.current();
        saveResolveRef.current = null;
        saveRejectRef.current = null;
      }
      toast.success("Code saved successfully");
      if (activeFileId) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setOriginalByFileId((prev) => ({
          ...prev,
          [activeFileId]: contentByFileId[activeFileId] ?? "",
        }));
        refetchMetadataFile();
      }
      if (data?.fileName && data?.metadataType) {
        onFileSaved?.({
          id: data.id,
          fileName: data.fileName,
          metadataType: data.metadataType,
        });
      }
    },
    onError: (error) => {
      if (saveRejectRef.current) {
        saveRejectRef.current(error);
        saveResolveRef.current = null;
        saveRejectRef.current = null;
        return;
      }
      const currentId = activeFileIdRef.current;
      const currentCode = currentId
        ? (contentByFileIdRef.current[currentId] ?? "")
        : "";
      if (currentId && currentCode && autoFixRef.current?.startAutoFix) {
        autoFixRef.current.startAutoFix(
          currentCode,
          error.message || "Compilation failed",
        );
        return;
      }
      toast.error(error.message || "Failed to save code");
    },
  });

  const getReferenceFiles = useCallback(() => {
    const id = activeFileIdRef.current;
    const content = contentByFileIdRef.current;
    return openFiles
      .filter((f) => f.id !== id)
      .map((f) => ({ file_name: f.name, content: content[f.id] ?? "" }))
      .filter((r) => r.content.length > 0);
  }, [openFiles]);

  const { autoFixState, startAutoFix, cancelAutoFix, resetAutoFix } =
    useAutoFixLoop({
      maxAttempts: 5,
      onCodeFixed: (fixedCode) => {
        if (activeFileId) {
          setContentByFileId((prev) => ({ ...prev, [activeFileId]: fixedCode }));
          setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: true }));
        }
      },
      onSave: () =>
        new Promise<void>((resolve, reject) => {
          if (!activeFileId) {
            reject(new Error("No active file"));
            return;
          }
          saveResolveRef.current = resolve;
          saveRejectRef.current = reject;
          updateMetadataMutation.mutate({
            id: activeFileId,
            content: contentByFileId[activeFileId] ?? "",
          });
        }),
      fileName: activeFile?.name,
      metadataType: activeFile?.metadataType,
      orgId,
      getReferenceFiles,
    });

  useEffect(() => {
    autoFixRef.current = { startAutoFix };
  }, [startAutoFix]);

  const lintApi = useApexLint();
  const { markers: analysisMarkers } = useApexAnalysis();

  const coverageMarkers = useMemo(
    () => buildCoverageMarkers(covData, activeFile),
    [covData, activeFile],
  );

  useEffect(() => {
    resetAutoFix();
  }, [activeFileId, resetAutoFix]);

  useEffect(() => {
    setViewingMain(false);
  }, [activeFileId]);

  const activeInitialContent = activeFileId
    ? initialContent?.[activeFileId] || virtualFiles[activeFileId]?.content
    : undefined;
  useEffect(() => {
    if (!activeFileId) return;

    if (activeInitialContent !== undefined) {
      const ic = activeInitialContent;
      setContentByFileId((prev) => {
        if (prev[activeFileId] !== undefined) return prev;
        return { ...prev, [activeFileId]: ic };
      });
      setOriginalByFileId((prev) => {
        if (prev[activeFileId] !== undefined) return prev;
        return { ...prev, [activeFileId]: ic };
      });
      return;
    }

    if (metadataFileData?.content === undefined) return;
    const mainContent = metadataFileData.content ?? "";
    const displayContent = activeBranch
      ? (activeBranch.content ?? mainContent)
      : mainContent;
    setContentByFileId((prev) => {
      if (prev[activeFileId] !== undefined) return prev;
      return { ...prev, [activeFileId]: displayContent };
    });
    // When activeBranch changes (e.g. after save creates a new branch),
    // update the original to reflect the branch baseline. Without this,
    // Compare shows stale diffs because originalByFileId was set on first
    // load and never updated when the branch was created/updated.
    const baseline = activeBranch ? (activeBranch.content ?? mainContent) : mainContent;
    setOriginalByFileId((prev) => {
      // First load: set initial value
      if (prev[activeFileId] === undefined) {
        return { ...prev, [activeFileId]: baseline };
      }
      // Branch changed: update baseline (e.g. after save created a new branch)
      if (activeBranch && prev[activeFileId] !== baseline) {
        return { ...prev, [activeFileId]: baseline };
      }
      return prev;
    });
  }, [activeFileId, activeInitialContent, metadataFileData?.content, activeBranch]);

  useEffect(() => {
    if (activeFileId === null && openFiles.length > 0) {
      onActiveFileChange(openFiles[0].id);
    }
  }, [activeFileId, openFiles, onActiveFileChange]);

  const handleClose = useCallback(() => {
    const isRules = openFiles.some((f) => f.name === "rules.cls");
    if (!isRules) {
      const anyDirty = Object.values(dirtyByFileId).some(Boolean);
      if (
        anyDirty &&
        !window.confirm("You have unsaved changes. Close anyway?")
      )
        return;
    }
    disposeStandaloneDiffEditor(diffEditorRef);
    onAcceptedCodeChange(null);
    onClose();
  }, [openFiles, dirtyByFileId, onClose, onAcceptedCodeChange]);

  const handleToggleViewMain = useCallback(() => {
    if (!activeBranch || !activeFileId) return;
    setViewingMain((prev) => !prev);
  }, [activeBranch, activeFileId]);

  const handleAbandon = useCallback(() => {
    if (!activeBranch) return;
    abandonBranch({ branchId: activeBranch.id });
  }, [activeBranch, abandonBranch]);

  const handleSave = useCallback(async () => {
    if (!activeFileId || isSaving || isBranchSaving || viewingMain) return;
    if (saveInFlightRef.current) return;

    const content =
      contentByFileId[activeFileId] ?? metadataFileData?.content;
    if (content == null) {
      toast.error("No content to save");
      return;
    }

    saveInFlightRef.current = true;
    try {
      if (activeFile?.name === "rules.cls") {
        try {
          const { getUserRules, addUserRule, deleteUserRule } = await import(
            "../../devAgentService"
          );
          const userId =
            (await import("@/stores/authStore_v1")).useAuthStoreV1.getState()
              .user?.id || "";
          if (!userId) {
            toast.error("Not logged in");
            return;
          }
          const selectedOrgId = selectedOrg?.id || "";
          if (!selectedOrgId) {
            toast.error("No org selected");
            return;
          }
          const existing = await getUserRules(selectedOrgId);
          for (const r of existing) await deleteUserRule(r.id, selectedOrgId);
          if (content.trim()) await addUserRule(selectedOrgId, content.trim());
          setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
          toast.success("Rules saved");
        } catch {
          toast.error("Failed to save rules");
        }
        return;
      }
      const className = _stripKnownMetadataExtension(activeFile?.name || activeFileId);
      const metadataType = activeFile?.metadataType || "ApexClass";

      if (!sessionUuid) {
        setDirtyByFileId((prev) => ({ ...prev, [activeFileId]: false }));
        setOriginalByFileId((prev) => ({ ...prev, [activeFileId]: content }));
        toast.success(
          "Saved locally — send a message to start a session for Salesforce validation",
        );
        return;
      }

      // Virtual files (AI-generated, not yet in ncoder-backend): before
      // saving as a branch we need to create the parent metadata_item row
      // and resolve its real id. Non-virtual files skip this entirely and
      // just use activeFileId.
      const ensureMetadataItemId = isVirtualFile
        ? async (): Promise<string | null> => {
            // Prefer the auth store's latestMetadataRetrieval — it reflects the
            // last COMPLETED retrieval for this org. The progress store's
            // selectedRetrievalId can be stale (e.g., pointing at a cancelled
            // or aborted retrieval that no longer exists in ncoder-backend).
            const retrievalId =
            selectedOrg?.latestMetadataRetrieval?.id ||
              selectedRetrievalId;
            if (!selectedOrg?.id || !retrievalId) {
              toast.error(
                "No metadata retrieval found. Retrieve metadata first before saving a new class.",
              );
              return null;
            }
            await createMetadataItemAsync({
              organizationId: selectedOrg.id,
              retrievalId,
              metadataType,
              fullName: className,
              fileName: _fileNameForNewMetadata(className, metadataType),
              content,
              deployToSalesforce: false,
            });
            const search = await graphqlRequest<{
              searchMetadataItems: Array<{
                id: string;
                fullName: string;
                metadataType: string;
              }>;
            }>(SEARCH_METADATA_ITEMS_QUERY, {
              organizationId: selectedOrg.id,
              retrievalId,
              searchString: className,
            });
            const created = search.searchMetadataItems?.find(
              (i) => i.fullName === className && i.metadataType === metadataType,
            );
            return created?.id ?? null;
          }
        : undefined;

      await runSessionSaveWithValidation({
        sessionUuid,
        activeFileId,
        className,
        metadataType,
        content,
        activeBranch,
        saveBranchAsync,
        ensureMetadataItemId,
        setIsSaving,
        setSaveErrors,
        setContentByFileId,
        setDirtyByFileId,
        setOriginalByFileId,
        onFileSaved,
        onTabIdResolved: onCanonicalFileId,
      });
    } finally {
      saveInFlightRef.current = false;
    }
  }, [
    activeFileId,
    contentByFileId,
    metadataFileData?.content,
    activeFile,
    onFileSaved,
    isSaving,
    isBranchSaving,
    viewingMain,
    activeBranch,
    saveBranchAsync,
    createMetadataItemAsync,
    isVirtualFile,
    sessionUuid,
    selectedOrg,
    selectedRetrievalId,
    onCanonicalFileId,
  ]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      disposeStandaloneDiffEditor(diffEditorRef);
      onActiveFileChange(tabId);
    },
    [onActiveFileChange],
  );
  const handleTabClose = useCallback(
    (tabId: string) => {
      disposeStandaloneDiffEditor(diffEditorRef);
      onCloseTab(tabId);
    },
    [onCloseTab],
  );
  const handleContentChange = useCallback((tabId: string, value: string) => {
    setContentByFileId((prev) => ({ ...prev, [tabId]: value }));
    setDirtyByFileId((prev) => ({ ...prev, [tabId]: true }));
    if (artifactBadges[tabId] === "R") {
      setArtifactBadge(tabId, "M");
    }
  }, []);

  const { mutateAsync: formatCodeAsync } = useFormatCode();
  const formatApi: FormatApi | undefined = useMemo(() => {
    if (!activeFile) return undefined;
    return {
      format: async (code: string, _language: string) => {
        const formatLang = getFormatApiLanguage(
          activeFile.metadataType,
          activeFile.name,
        );
        if (!formatLang) {
          toast.error("Format not supported for this file type");
          throw new Error("Unsupported format language");
        }
        const result = await formatCodeAsync({ code, language: formatLang });
        if (result.error) toast.error(`Format: ${result.error}`);
        return result.formatted;
      },
    };
  }, [activeFile, formatCodeAsync]);

  const packageTabs: EditorTab[] = useMemo(
    () =>
      buildPackageTabs({
        openFiles,
        activeFileId,
        viewingMain,
        contentByFileId,
        dirtyByFileId,
        originalByFileId,
        initialContent,
        virtualFiles,
      }),
    [
      openFiles,
      activeFileId,
      viewingMain,
      contentByFileId,
      dirtyByFileId,
      originalByFileId,
      initialContent,
      virtualFiles,
    ],
  );

  // Match packageTabs isDirty: virtual / chat-seeded files need Save enabled
  // before the user types (toolbar + Cmd+S gate on isDirty).
  const isDirty =
    activeFileId && !viewingMain
      ? (dirtyByFileId[activeFileId] ??
          !!(initialContent?.[activeFileId] || virtualFiles[activeFileId]))
      : false;
  const originalContentForDiff = activeFileId
    ? (originalByFileId[activeFileId] ??
      metadataFileData?.content ??
      virtualFiles[activeFileId]?.content ??
      initialContent?.[activeFileId] ??
      "")
    : "";

  const showDiff =
    (!metadataFileError ||
      isVirtualFile ||
      contentByFileId[activeFileId ?? ""] !== undefined) &&
    acceptedCode != null &&
    acceptedCode !== "" &&
    !!activeFileId &&
    (!acceptedCodeTarget ||
      acceptedCodeTarget === activeFile?.name);

  const showTabbedEditor =
    (!metadataFileError ||
      isVirtualFile ||
      contentByFileId[activeFileId ?? ""] !== undefined) &&
    (acceptedCode == null || acceptedCode === "");


  return {
    shouldRenderNull: openFiles.length === 0,
    editorTheme,
    diffEditorRef,
    saveErrors,
    setSaveErrors,
    contentByFileId,
    setContentByFileId,
    setOriginalByFileId,
    setDirtyByFileId,
    isSaving,
    errorExpanded,
    setErrorExpanded,
    diffOverlayOpen,
    setDiffOverlayOpen,
    diffHunkTotal,
    diffHunkResolved,
    setDiffHunkTotal,
    setDiffHunkResolved,
    viewingMain,
    activeFile,
    activeBadge,
    virtualFiles,
    isVirtualFile,
    metadataFileData,
    isLoadingMetadataFile,
    metadataFileError,
    activeBranch,
    handleClose,
    handleToggleViewMain,
    handleAbandon,
    handleSave,
    handleTabChange,
    handleTabClose,
    handleContentChange,
    formatApi,
    lintApi,
    packageTabs,
    isDirty,
    originalByFileId,
    originalContentForDiff,
    showDiff,
    showTabbedEditor,
    autoFixState,
    cancelAutoFix,
    resetAutoFix,
    analysisMarkers,
    coverageMarkers,
    covData,
    isAbandoning,
    isBranchSaving,
    editorOptions: METADATA_TABBED_EDITOR_OPTIONS,
    extraActions,
    isAnalyzing,
    onRunTest,
    isBusy,
    onLoadIntoChat,
    acceptedCode,
    onAcceptedCodeChange,
  };
}
